import React, { useState, useEffect } from "react";
import { Dropdown, Menu } from "antd";
import { BsThreeDotsVertical } from "react-icons/bs";
import { IoLocationSharp } from "react-icons/io5";

import editIcon from "../../../Assets/icons/ink_pen.svg";
import locIcon from "../../../Assets/icons/Location.svg";
import CalIcon from "../../../Assets/icons/Calendar.svg";
import ClockIcon from "../../../Assets/icons/Clock Circle.svg";
import delIcon from "../../../Assets/icons/Vector.svg";

import "./style.css";
import {
  Button,
  Input,
  Table,
  Popconfirm,
  Form,
  Modal,
  Upload,
  message,
  Empty,
} from "antd";
import {
  UploadOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import LocationModal from "./LocationModal";
import {
  getFuelStations,
  createFuelStation,
  deleteFuelStation,
  updateFuelStation,
  bulkCreateFuelStations,
} from "../../../API/Admin/fuelStation";

const FuelStations = () => {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [form] = Form.useForm();
  const [openLocationModal, setOpenLocationModal] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [locationValue, setLocationValue] = useState("");

  const [fetchError, setFetchError] = useState(false);

  const fetchUpdatedFuelStations = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const response = await getFuelStations();
      if (Array.isArray(response?.data)) {
        setStations(response?.data);
      } else {
        throw new Error("Invalid response structure");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setFetchError(true);
      message.error("Failed to fetch fuel stations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpdatedFuelStations();
  }, []);

  if (fetchError) {
    return (
      <div
        style={{
          height: "250px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Empty description="Failed to load fuel stations due to a server error." />
      </div>
    );
  }

  const openAddForm = () => {
    setEditingStation(null);
    form.resetFields();
    setAddModalVisible(true);
  };

  const openEditForm = (record) => {
    const formattedRecord = {
      ...record,
      fuel_price_date: record.fuel_price_date?.split("T")[0] || "",
      time: record.time?.substring(0, 5) || "",
    };
    setEditingStation(record);
    form.setFieldsValue(formattedRecord);
    setLocationValue(formattedRecord.location || "");
    setAddModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteFuelStation(id);
      message.success("Deleted successfully");
      fetchUpdatedFuelStations();
    } catch {
      message.error("Failed to delete fuel station");
    }
  };

  const handleAddEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        region_name: values.region_name,
        market_company: values.market_company,
        gas_station: values.gas_station,
        location: values.location,
        fuel_type: values.fuel_type,
        fuel_price: parseFloat(values.fuel_price),
        fuel_price_date: values.fuel_price_date,
        time: values.time,
      };
      if (editingStation) {
        await updateFuelStation(editingStation.id, payload);
        message.success("Fuel station updated");
      } else {
        await createFuelStation(payload);
        message.success("Fuel station created");
      }
      setAddModalVisible(false);
      fetchUpdatedFuelStations();
    } catch {
      message.error("Failed to save fuel station");
    }
  };

  const showImportModal = () => {
    setImportModalVisible(true);
    setImportResults(null);
    setFileList([]);
  };

  const handleImportCancel = () => {
    setImportModalVisible(false);
    setFileList([]);
    setImportResults(null);
  };

  const beforeUpload = (file) => {
    const isExcel =
      file.type.includes("excel") ||
      file.type.includes("spreadsheet") ||
      [".xlsx", ".xls"].some((ext) => file.name.endsWith(ext));
    if (!isExcel) message.error("You can only upload Excel files!");
    return isExcel;
  };

  const handleFileChange = ({ fileList }) => setFileList(fileList);

  const handleBulkImport = async () => {
    if (fileList.length === 0) {
      message.warning("Please select a file first");
      return;
    }

    const file = fileList[0].originFileObj;
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        setImporting(true);
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const headerRow = jsonData.find((row) =>
          row.some(
            (cell) => String(cell).trim().toLowerCase() === "region name"
          )
        );

        if (!headerRow) {
          message.error("No valid header row found in Excel file");
          return;
        }

        const headerMap = {};
        headerRow.forEach((cell, index) => {
          const normalized = String(cell)
            .trim()
            .toLowerCase()
            .replace(/\s+/g, " ")
            .replace(/\./g, "");
          headerMap[normalized] = index;
        });

        const requiredColumns = [
          "region name",
          "market company",
          "gas station",
          "address",
        ];
        const missingColumns = requiredColumns.filter(
          (col) => !(col in headerMap)
        );

        if (missingColumns.length > 0) {
          message.error(`Missing columns: ${missingColumns.join(", ")}`);
          return;
        }

        const dataStartIndex = jsonData.indexOf(headerRow) + 1;
        const stations = jsonData
          .slice(dataStartIndex)
          .map((row) => {
            const get = (col) => {
              const index = headerMap[col];
              return index !== undefined ? row[index] : undefined;
            };

            const gasStation = String(get("gas station") || "").trim();
            const address = String(get("address") || "").trim();

            if (!gasStation || !address) return null;

            const fuelPriceStr = String(get("fuel price") || "0").replace(
              /[^0-9.]/g,
              ""
            );
            const fuelPrice = parseFloat(fuelPriceStr) || 0;

            let fuelPriceDate = get("fuel price date");
            if (fuelPriceDate instanceof Date) {
              fuelPriceDate = fuelPriceDate.toISOString().split("T")[0];
            } else if (typeof fuelPriceDate === "string") {
              const [month, day, year] = fuelPriceDate.split("/");
              if (month && day && year) {
                fuelPriceDate = `${year}-${month.padStart(
                  2,
                  "0"
                )}-${day.padStart(2, "0")}`;
              }
            }

            return {
              region_name: String(get("region name") || "").trim(),
              market_company: String(get("market company") || "").trim(),
              gas_station: gasStation,
              address: address,
              location: String(get("gps location") || "").trim(),
              fuel_type: String(get("fuel type") || "").trim(),
              fuel_price: fuelPrice,
              fuel_price_date: fuelPriceDate || "",
            };
          })
          .filter((station) => station !== null);

        if (stations.length === 0) {
          message.error(
            `No valid data found. Verify that:\n1. First sheet contains data\n2. Required columns exist: ${requiredColumns.join(
              ", "
            )}\n3. At least one row has Gas Station and Address values`
          );
          return;
        }

        const response = await bulkCreateFuelStations({ stations });
        if (response.data) {
          message.success(`Successfully imported ${stations.length} stations`);
          fetchUpdatedFuelStations();
          setImportModalVisible(false);
        }
      } catch (err) {
        message.error(`Import failed: ${err.message}`);
        console.error("Import error details:", err);
      } finally {
        setImporting(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const columns = [
    {
      title: "Region Name",
      dataIndex: "region_name",
      key: "region_name",
      className: "region-cell",
    },
    {
      title: "Market Company",
      dataIndex: "market_company",
      key: "market_company",
    },
    { title: "Gas Station", dataIndex: "gas_station", key: "gas_station" },
    { title: "Address", dataIndex: "address", key: "address" },
    { title: "Location", dataIndex: "location", key: "location" },
    { title: "Fuel Type", dataIndex: "fuel_type", key: "fuel_type" },
    { title: "Fuel Price", dataIndex: "fuel_price", key: "fuel_price" },
    {
      title: "Fuel Price Date",
      dataIndex: "fuel_price_date",
      key: "fuel_price_date",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-"),
    },
    {
      title: "Time",
      dataIndex: "time",
      key: "time",
      render: (time) => time || "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const menu = (
          <Menu className="fuel-station-dropdown-menu">
            <Menu.Item key="edit" onClick={() => openEditForm(record)}>
              <span className="actions-item-spc">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.11526 8.03609L10.0308 8.95159L15.6578 3.32459L14.7423 2.40909L9.11526 8.03609ZM2.06726 15.9996H2.98276L8.95201 10.0303L8.03651 9.11484L2.06726 15.0841V15.9996ZM10.5578 10.5573L7.51926 7.50934L12.002 3.02659L11.4598 2.48434C11.4084 2.433 11.3315 2.40734 11.229 2.40734C11.1263 2.40734 11.0493 2.433 10.998 2.48434L5.82126 7.66109L4.76751 6.62659L9.94826 1.43034C10.3098 1.06884 10.7373 0.888086 11.2308 0.888086C11.7244 0.888086 12.152 1.06884 12.5135 1.43034L13.0558 1.97259L14.0943 0.934337C14.2751 0.753504 14.4885 0.663086 14.7345 0.663086C14.9807 0.663086 15.1942 0.753504 15.375 0.934337L17.171 2.73034C17.3518 2.91117 17.4391 3.11825 17.4328 3.35159C17.4263 3.58492 17.3327 3.79192 17.152 3.97259L10.5578 10.5573ZM3.60576 17.4996H0.567261V14.4611L7.50976 7.50934L10.5578 10.5573L3.60576 17.4996Z"
                    fill="#DEDEDE"
                  />
                </svg>
                Edit
              </span>
            </Menu.Item>

            <Menu.Item key="delete" onClick={() => handleDelete(record.id)}>
              <span className="actions-item-spc">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.22363 0.00257456C9.7312 -0.0236624 10.2294 0.151464 10.6084 0.489879C10.9872 0.828222 11.217 1.3024 11.248 1.80922V3.00062C11.2488 3.05484 11.2403 3.10855 11.2266 3.16078H11.874C11.8999 3.15763 11.9258 3.15297 11.9521 3.15297C11.9785 3.15297 12.0044 3.15761 12.0303 3.16078H13.4004C13.5594 3.16088 13.7118 3.22419 13.8242 3.33656C13.9367 3.44899 14 3.60139 14 3.76039C14 3.91937 13.9366 4.07176 13.8242 4.18422C13.7118 4.29659 13.5594 4.35989 13.4004 4.36H12.5996V12.1881C12.5854 12.4417 12.5211 12.6901 12.4102 12.9186C12.2993 13.1469 12.1444 13.3512 11.9541 13.5192C11.7636 13.6873 11.5414 13.8164 11.3008 13.8981C11.0602 13.9798 10.8053 14.0131 10.5518 13.9957H3.44824C3.19472 14.0131 2.93983 13.9798 2.69922 13.8981C2.45865 13.8164 2.23637 13.6872 2.0459 13.5192C1.85558 13.3512 1.70072 13.1469 1.58984 12.9186C1.4789 12.6901 1.41462 12.4417 1.40039 12.1881V4.36H0.599609C0.520949 4.35995 0.442792 4.34419 0.370117 4.3141C0.297477 4.28396 0.231396 4.2398 0.175781 4.18422C0.120136 4.12856 0.0760245 4.06257 0.0458984 3.98988C0.0157522 3.91713 0 3.83913 0 3.76039C1.57001e-05 3.68167 0.0157609 3.60362 0.0458984 3.53089C0.0760444 3.45822 0.120126 3.39219 0.175781 3.33656C0.231417 3.28095 0.297444 3.23681 0.370117 3.20668C0.442792 3.17659 0.520949 3.16083 0.599609 3.16078H2.80078C2.78634 3.1089 2.77637 3.05542 2.77637 3.00062V1.80922C2.8074 1.30673 3.03364 0.836342 3.40723 0.498668C3.78103 0.160919 4.27285 -0.0171864 4.77637 0.00257456H9.22363ZM2.59961 12.1881C2.59961 12.516 2.97639 12.7955 3.40039 12.7955H10.5039C10.9519 12.7955 11.3037 12.516 11.3037 12.1881V4.36H2.59961V12.1881ZM5.57617 6.17543C5.73506 6.17555 5.88764 6.23885 6 6.35121C6.1123 6.46365 6.17578 6.61611 6.17578 6.77504V10.3893C6.17682 10.4686 6.16142 10.5472 6.13184 10.6207C6.10223 10.6943 6.05856 10.7615 6.00293 10.818C5.94714 10.8745 5.87993 10.9202 5.80664 10.9508C5.73364 10.9813 5.65527 10.9967 5.57617 10.9967C5.41787 10.9946 5.2663 10.9309 5.1543 10.819C5.04228 10.707 4.97766 10.5555 4.97559 10.3971V6.77504C4.97559 6.61612 5.03908 6.46364 5.15137 6.35121C5.26383 6.23874 5.41712 6.17543 5.57617 6.17543ZM8.42383 6.17543C8.58284 6.17545 8.73521 6.23876 8.84766 6.35121C8.96007 6.46367 9.02344 6.61602 9.02344 6.77504V10.3893C9.02341 10.549 8.96073 10.7024 8.84863 10.8161C8.73641 10.9298 8.58357 10.9946 8.42383 10.9967C8.26479 10.9967 8.11246 10.9334 8 10.8209C7.88753 10.7085 7.82422 10.5562 7.82422 10.3971V6.77504C7.82422 6.61599 7.88753 6.46367 8 6.35121C8.11246 6.23875 8.26479 6.17543 8.42383 6.17543ZM4.77637 1.20179C4.33648 1.20179 3.97577 1.48149 3.97559 1.80922V3.00062C3.97559 3.05534 3.96655 3.10897 3.95215 3.16078H10.0713C10.0573 3.10847 10.0471 3.055 10.0479 3.00062V1.80922C10.0477 1.48149 9.66393 1.2018 9.24805 1.20179H4.77637Z"
                    fill="#FB3748"
                  />
                </svg>
                Delete
              </span>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button
              type="text"
              icon={<BsThreeDotsVertical size={18} color="#8B899F" />}
              style={{ padding: 0 }}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="fuel-station-admin">
      <div className="page-header">
        <div className="title-wrapper">
          <div className="page-title">Fuel Stations</div>
          <div className="title-description">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>
        </div>
        <div className="button-group">
          <Button type="primary" className="add-btn" onClick={openAddForm}>
            Add Fuel Station
          </Button>

          <Button className="import-btn" onClick={showImportModal}>
            Import from Excel
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={stations}
        loading={loading}
        rowKey="id"
        scroll={{ x: true }}
      />

      <Modal
        title={editingStation ? "Edit Fuel Station" : "Add Fuel Station"}
        open={addModalVisible}
        onOk={handleAddEditSubmit}
        onCancel={() => setAddModalVisible(false)}
        confirmLoading={loading}
        okText={editingStation ? "Save Changes" : "Create"}
        cancelText="Cancel"
        okButtonProps={{ className: "fuel-ok-button" }}
        cancelButtonProps={{ className: "fuel-cancel-button" }}
        className="fuel-modal"
      >
        <Form form={form} layout="vertical">
          <div className="fuel-item">
            <div className="fuel-item-wrapper">
              <Form.Item
                name="region_name"
                label="Region Name"
                rules={[{ required: true }]}
                className="form-item"
                style={{ width: "278px" }}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="market_company"
                label="Market Company"
                rules={[{ required: true }]}
                className="form-item"
                style={{ width: "278px" }}
              >
                <Input />
              </Form.Item>
            </div>
            <Form.Item
              name="gas_station"
              label="Gas Station"
              rules={[{ required: true }]}
              className="form-item"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="address"
              label="Address"
              rules={[{ required: true }]}
              className="form-item"
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="location"
              label="Location"
              rules={[{ required: true }]}
            >
              <Input
                value={locationValue}
                readOnly
                placeholder="Click the pin icon to select coordinates"
                addonAfter={
                  <IoLocationSharp
                    onClick={() => setOpenLocationModal(true)}
                    style={{ cursor: "pointer" }}
                  />
                }
              />
            </Form.Item>
            <div className="Second-wrapper">
              <Form.Item
                name="fuel_type"
                label="Fuel Type"
                rules={[{ required: true }]}
                className="form-item"
                style={{ width: "278px" }}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="fuel_price"
                label="Fuel Price"
                rules={[{ required: true }]}
                className="form-item"
                style={{ width: "278px" }}
              >
                <Input />
              </Form.Item>
            </div>
            <div className=" Third-wrapper">
              <Form.Item
                name="fuel_price_date"
                label="Fuel Price Date"
                rules={[{ required: true }]}
                className="form-item"
                style={{ width: "278px" }}
              >
                <Input type="date" />
              </Form.Item>
              <Form.Item
                name="time"
                label="Time"
                rules={[{ required: true }]}
                className="form-item"
                style={{ width: "278px" }}
              >
                <Input type="time" />
              </Form.Item>
            </div>
          </div>
        </Form>
      </Modal>

      <LocationModal
        open={openLocationModal}
        setOpen={setOpenLocationModal}
        setLocation={(loc) => {
          setLocationValue(loc);
          form.setFieldValue("location", loc);
        }}
      />

      <Modal
        className="fuel-modal"
        title="Import Fuel Stations from Excel"
        open={importModalVisible}
        onCancel={handleImportCancel}
        onOk={handleBulkImport}
        confirmLoading={importing}
      >
        <Upload
          beforeUpload={beforeUpload}
          onChange={handleFileChange}
          fileList={fileList}
          maxCount={1}
        >
          <Button
            icon={<UploadOutlined />}
            style={{ marginTop: 10, marginBottom: 8 }}
          >
            Select Excel File
          </Button>
        </Upload>

        <p style={{ marginTop: 10 }}>
          Ensure the first sheet contains valid headers: Region Name, Market
          Company, Gas Station, Address.
        </p>
      </Modal>
    </div>
  );
};

export default FuelStations;
